import Vue from "vue";
import {
    FetchState,
    DrivewealthAccountPerformanceRequestArgs,
    DrivewealthAccountPerformance,
    DrivewealthAccountPerformanceState,
    DrivewealthAccountPerformanceFrequency,
    DrivewealthAccountIdentifierArgs,
    DrivewealthAccountPerformanceTimePeriod
} from "@/shared/types";
import * as storeHelper from "@/shared/storeHelper";
import { defineStore } from "pinia";
import _ from "lodash";
import { templatedFetchKeyCollectionStore } from "@/shared/store/fetchKeyedCollectionPlugin";
import * as drivewealthService from "@/shared/api/drivewealthService";
import moment from "moment/moment";

const dateFormatString = "YYYY-MM-DD";

const firstDayOfYear = moment().subtract(
    moment().diff(moment().startOf("year"), "days"), "months")
    .format(dateFormatString);

export const initialState: DrivewealthAccountPerformanceState = {
    selectedFrequency: DrivewealthAccountPerformanceFrequency.Monthly,
    selectedTimePeriod: DrivewealthAccountPerformanceTimePeriod.YearToDate,
    fromDate: firstDayOfYear,
    selectedAccountId: 0
};

const useAccountPerformanceDataSubStore = defineStore("drivewealth_accountPerformanceData", () => {
    return templatedFetchKeyCollectionStore<DrivewealthAccountPerformance, DrivewealthAccountPerformance>(
        drivewealthService.getAccountPerformanceData,
    );
});

const accountPerformanceItemKeyFormatter = (args: DrivewealthAccountPerformanceRequestArgs): string => {
    return `${args.accountId}_${args.frequency}_${args.fromDate}_${args.toDate}`;
};

export const useDrivewealthAccountPerformanceStore = defineStore("drivewealthAccountPerformance", {
    persist: {
        storage: localStorage,
        paths: storeHelper.getStoragePaths("drivewealthAccountPerformance"),
    },
    state: () => (_.cloneDeep(initialState)),
    getters: {
        accountPerformanceDataStore: () => useAccountPerformanceDataSubStore(),
        accountPerformanceRequestModel (): (args: DrivewealthAccountIdentifierArgs) => DrivewealthAccountPerformanceRequestArgs {
            return (args: DrivewealthAccountIdentifierArgs) => {
                const requestModel: DrivewealthAccountPerformanceRequestArgs = {
                    ...args,
                    frequency: this.selectedFrequency,
                    fromDate: this.fromDate,
                    toDate: moment().format(dateFormatString),
                };

                return requestModel;
            };
        },
        accountPerformanceLoaded (): (args: DrivewealthAccountIdentifierArgs) => boolean {
            return (args: DrivewealthAccountIdentifierArgs) => {
                const requestModel = this.accountPerformanceRequestModel(args);
                const itemKey = accountPerformanceItemKeyFormatter(requestModel);
                const performanceLookup = this.accountPerformanceDataStore.fetchStates[itemKey];

                if (performanceLookup) {
                    return performanceLookup === FetchState.Loaded;
                }

                return false;
            };
        },
        accountPerformanceLoading (): (args: DrivewealthAccountIdentifierArgs) => boolean {
            return (args: DrivewealthAccountIdentifierArgs) => {
                const requestModel = this.accountPerformanceRequestModel(args);
                const itemKey = accountPerformanceItemKeyFormatter(requestModel);
                const performanceLookup = this.accountPerformanceDataStore.fetchStates[itemKey];

                if (performanceLookup) {
                    return performanceLookup === FetchState.Loading;
                }

                return false;
            };
        },
        accountPerformanceResult (): (args: DrivewealthAccountIdentifierArgs) => DrivewealthAccountPerformance {
            return (args: DrivewealthAccountIdentifierArgs) => {
                const requestModel = this.accountPerformanceRequestModel(args);
                const itemKey = accountPerformanceItemKeyFormatter(requestModel);
                const performanceLookup = this.accountPerformanceDataStore.items[itemKey];

                if (performanceLookup) {
                    return performanceLookup;
                }

                return null;
            };
        },
    },
    actions: {
        setFromDate (date: string) {
            this.fromDate = date;
        },
        setSelectedFrequency (frequency: DrivewealthAccountPerformanceFrequency) {
            this.selectedFrequency = frequency;
        },
        setSelectedTimePeriod (timePeriod: DrivewealthAccountPerformanceTimePeriod) {
            this.selectedTimePeriod = timePeriod;
        },
        setSelectedAccountId (accountId: number) {
            this.selectedAccountId = accountId;
        },
        async getAccountPerformance (
            args: DrivewealthAccountIdentifierArgs
        ): Promise<any> {
            const requestModel = this.accountPerformanceRequestModel(args);
            const itemKey = accountPerformanceItemKeyFormatter(requestModel);
            return useAccountPerformanceDataSubStore().fetchKeyedApiCall(
                { username: requestModel.username, partnerId: requestModel.partnerId },
                itemKey,
                requestModel
            )
                .catch(err => {
                    return storeHelper.handleError({ error: err, rethrow: true });
                });
        },
    }
});
